import React, { useMemo, useState } from "react";
import { CLINIC_ROUTES, ROOTS, SETTINGS_ROUTES } from "@app/config/constants";
import { getClinicNavigationItems } from "@features/clinics/utils";
import { Badge, Navigation } from "@medentic/polaris-react";
import { 
  OrganizationIcon, 
  SettingsFilledIcon,
  MobileIcon
} from "@medentic/polaris-icons";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUserPermissions } from "@shared/hooks/user/useUserPermissions";
import usePerks from "@features/clinics/hooks/usePerks";
import { useIsMobile } from "@shared/hooks/navigation/useIsMobile";
import { usePWAInstall } from "@shared/hooks/navigation/usePWAInstall";
import { PWAInstall } from "@shared/ui";
import { HealthcareCompany, HealthcareCompanyType } from "@shared/types/commonTypes";
import { useQueryClient } from "@tanstack/react-query";

const ClinicNavigation: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const params = useParams();
  const selectedClinicId = params.clinicId;
  const { userPermissions = [] } = useUserPermissions();
  const { perks = [] } = usePerks();
  const isMobile = useIsMobile();
  const { isInstallAvailable } = usePWAInstall();
  const [showPWAInstall, setShowPWAInstall] = useState(false);
  const queryClient = useQueryClient();
  const showPerksNew = useMemo(() => {
    return !!perks.find((p) => p.showAsNew);
  }, [perks]);

  const clinics = queryClient.getQueryData<HealthcareCompany[]>(["healthcare-companies", HealthcareCompanyType.DENTAL_CLINIC]) || [];

  const handlePWAInstallClick = () => {
    setShowPWAInstall(true);
  };

  const handleClosePWAInstall = () => {
    setShowPWAInstall(false);
  };

  const clinicNavigationItems = useMemo(() => {
    if (!selectedClinicId) return [];

    const items = getClinicNavigationItems(
      t,
      selectedClinicId,
      location.pathname,
      showPerksNew ? (
        <Badge tone="info" progress="complete" size="small" />
      ) : undefined,
      isMobile
    );

    // Filter main navigation items
    const filteredItems = items.filter(
      (item) => !item.adminOnly || userPermissions.includes("update_company")
    );

    // Filter sub-navigation items if they exist
    return filteredItems.map((item) => ({
      ...item,
      subNavigationItems: item.subNavigationItems?.filter(
        (subItem) =>
          !subItem.adminOnly || userPermissions.includes("update_company")
      ),
    }));
  }, [
    t,
    selectedClinicId,
    location.pathname,
    showPerksNew,
    userPermissions,
    isMobile,
  ]);

  if (!selectedClinicId) {
    return null;
  }

  return (
    <>
      <Navigation location={location.pathname}>
        {/* PWA Install section for mobile */}
        {((isMobile && isInstallAvailable)) && (
          <Navigation.Section
            items={[
              {
                label: t("pwa.installApp"),
                icon: MobileIcon,
                onClick: handlePWAInstallClick,
              },
            ]}
            separator
          />
        )}
        
        <Navigation.Section items={clinicNavigationItems} fill={!isMobile} />
        
        {isMobile && clinics.length > 1 && (
          <Navigation.Section
            fill
            items={[
              {
                label: t("topbar.clinics"),
                icon: OrganizationIcon,
                url: "#",
                subNavigationItems: clinics.map((c) => ({
                  label: c.name,
                  url: ROOTS.CLINIC + "/" + c.id,
                })),
              },
            ]}
            separator
          />
        )}
        
        {userPermissions.includes("update_company") && (
          <Navigation.Section
            items={[
              {
                label: t("clinicNavigation.settings"),
                icon: SettingsFilledIcon,
                url: `${ROOTS.CLINIC}/${selectedClinicId}/${CLINIC_ROUTES.SETTINGS}/${SETTINGS_ROUTES.DETAILS}`,
                selected:
                  location.pathname ===
                  `${ROOTS.CLINIC}/${selectedClinicId}/${CLINIC_ROUTES.SETTINGS}/${SETTINGS_ROUTES.DETAILS}`,
              },
            ]}
            separator
          />
        )}
      </Navigation>

      {/* PWA Install Modal */}
      <PWAInstall 
        isOpen={showPWAInstall} 
        onClose={handleClosePWAInstall} 
      />
    </>
  );
};

export default ClinicNavigation;
